import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Users, Anchor, Calendar, MessageSquare, Inbox, ShieldAlert, Star, MapPin,
  Crown, TrendingUp,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Avatar from "../components/Avatar";

function StatCard({ icon: Icon, label, value, sub, accent = "ocean" }) {
  const palette = {
    ocean: "from-ocean to-bretagne text-blanc",
    corail: "from-corail to-jaune text-blanc",
    turquoise: "from-turquoise to-ocean text-blanc",
    bretagne: "from-bretagne to-ocean text-blanc",
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${palette[accent]} shadow-matelot`}>
      <Icon className="absolute -top-2 -right-2 w-20 h-20 opacity-10" />
      <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{label}</div>
      <div className="font-display text-3xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs opacity-80 mt-1">{sub}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, children, action }) {
  return (
    <section className="bg-blanc border border-brume rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg font-bold text-ocean inline-flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5 text-corail" />} {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();

  useEffect(() => { document.title = "Modérateur — Le Bon Matelot"; }, []);
  useEffect(() => {
    if (!loading && !user) nav("/connexion?next=/admin");
  }, [loading, user, nav]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-stats"], enabled: !!user,
    queryFn: async () => (await api.get("/admin/stats")).data,
    refetchInterval: 60000,
    retry: false,
  });

  if (loading || (!user && !loading)) {
    return <div className="max-w-5xl mx-auto px-6 py-20 text-center text-bretagne">Chargement…</div>;
  }

  if (isError) {
    return (
      <div className="bg-blanc min-h-screen grid place-items-center px-6">
        <div className="text-center max-w-md">
          <ShieldAlert className="w-12 h-12 text-corail mx-auto" />
          <h1 className="font-display text-2xl font-bold text-ocean mt-4">Accès refusé</h1>
          <p className="text-ocean/70 mt-2 text-sm">
            Cette page est réservée au fondateur du site.
            {error?.response?.status === 401 && " Connectez-vous d'abord."}
          </p>
          <button
            onClick={() => nav("/")}
            className="mt-5 bg-ocean text-blanc px-6 py-2.5 rounded-full font-bold tactile"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-bretagne">Chargement des statistiques…</div>;
  }

  return (
    <div className="bg-blanc min-h-screen">
      <div className="relative bg-gradient-to-br from-ocean to-bretagne text-blanc overflow-hidden">
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-corail/30 blur-3xl" />
        <div className="absolute -top-20 -left-10 w-72 h-72 rounded-full bg-jaune/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-12">
          <div className="inline-flex items-center gap-2 bg-jaune text-ocean text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
            <Crown className="w-3.5 h-3.5" /> Espace fondateur
          </div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight">Tableau de bord</h1>
          <p className="text-blanc/80 mt-2 text-sm">
            Vue d'ensemble de la communauté · mise à jour il y a quelques secondes
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 space-y-8" data-testid="admin-dashboard">
        {/* Top stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Users} label="Inscrits" value={data.users.total}
            sub={`+${data.users.this_week} cette semaine · +${data.users.this_month} ce mois`}
            accent="ocean"
          />
          <StatCard
            icon={Anchor} label="Annonces" value={data.trips.total}
            sub={`${data.trips.by_status.active} actives · ${data.trips.by_status.cancelled} annulées`}
            accent="corail"
          />
          <StatCard
            icon={Inbox} label="Demandes" value={data.bookings.total}
            sub={`${data.bookings.by_status.confirmed} acceptées · ${data.bookings.by_status.pending} en attente`}
            accent="turquoise"
          />
          <StatCard
            icon={MessageSquare} label="Messages échangés" value={data.activity.messages}
            sub={`${data.activity.conversations} conversations · ${data.activity.reviews} avis`}
            accent="bretagne"
          />
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left col: ports + top captains */}
          <div className="lg:col-span-1 space-y-6">
            <Section title="Sorties par port" icon={MapPin}>
              {data.ports.length === 0 ? (
                <p className="text-sm text-bretagne">Aucune sortie publiée.</p>
              ) : (
                <ul className="space-y-2">
                  {data.ports.map((p) => {
                    const max = Math.max(...data.ports.map((x) => x.trips), 1);
                    const pct = Math.round((p.trips / max) * 100);
                    return (
                      <li key={p.port} data-testid={`port-${p.port}`}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-ocean font-semibold">{p.port}</span>
                          <span className="text-bretagne text-xs">{p.trips}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-brume overflow-hidden">
                          <div className="h-full bg-corail" style={{ width: `${pct}%` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>

            <Section title="Top capitaines" icon={TrendingUp}>
              {data.top_captains.length === 0 ? (
                <p className="text-sm text-bretagne">Pas encore de capitaine actif.</p>
              ) : (
                <ul className="space-y-3">
                  {data.top_captains.map((c) => (
                    <li key={c.user_id} className="flex items-center gap-3" data-testid={`top-captain-${c.user_id}`}>
                      <Avatar name={c.name} picture={c.picture} size={40} />
                      <div className="flex-1 min-w-0">
                        <a href={`/capitaine/${c.user_id}`} className="font-semibold text-ocean text-sm hover:text-corail truncate block">
                          {c.name}
                        </a>
                        <div className="text-[11px] text-bretagne truncate">{c.email}</div>
                      </div>
                      <div className="text-right text-xs">
                        <div className="font-display font-bold text-ocean">{c.trips}</div>
                        <div className="text-bretagne text-[10px]">sortie{c.trips > 1 ? "s" : ""}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>

          {/* Right col: latest signups */}
          <div className="lg:col-span-2">
            <Section title="Derniers inscrits" icon={Users} action={<span className="text-xs text-bretagne">20 plus récents</span>}>
              <div className="overflow-x-auto -mx-5 px-5">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-bretagne/80 font-bold border-b border-brume">
                      <th className="text-left py-2 pr-3">Membre</th>
                      <th className="text-left py-2 pr-3 hidden sm:table-cell">Email</th>
                      <th className="text-left py-2 pr-3 hidden md:table-cell">Port</th>
                      <th className="text-left py-2">Inscrit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brume/70">
                    {data.latest_signups.map((s) => (
                      <tr key={s.user_id} data-testid={`signup-${s.user_id}`}>
                        <td className="py-2.5 pr-3">
                          <div className="flex items-center gap-2.5">
                            <Avatar name={s.name} picture={s.picture} size={32} />
                            <div>
                              <div className="font-semibold text-ocean flex items-center gap-1.5">
                                {s.name}
                                {s.is_founder && (
                                  <Crown className="w-3 h-3 text-jaune fill-jaune" />
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 pr-3 hidden sm:table-cell text-bretagne text-xs truncate max-w-[180px]">{s.email}</td>
                        <td className="py-2.5 pr-3 hidden md:table-cell text-ocean/80 text-xs">{s.home_port || "—"}</td>
                        <td className="py-2.5 text-bretagne text-xs whitespace-nowrap">
                          {new Date(s.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>
        </div>

        {/* Status breakdown */}
        <div className="grid md:grid-cols-2 gap-6">
          <Section title="Annonces par statut" icon={Anchor}>
            <ul className="space-y-2 text-sm">
              {Object.entries(data.trips.by_status).map(([k, v]) => (
                <li key={k} className="flex items-center justify-between" data-testid={`trip-status-${k}`}>
                  <span className="capitalize text-ocean/80">{
                    { active: "Actives", paused: "En pause", complete: "Complètes", cancelled: "Annulées" }[k] || k
                  }</span>
                  <span className="font-display font-bold text-ocean">{v}</span>
                </li>
              ))}
            </ul>
          </Section>
          <Section title="Demandes par statut" icon={Inbox}>
            <ul className="space-y-2 text-sm">
              {Object.entries(data.bookings.by_status).map(([k, v]) => (
                <li key={k} className="flex items-center justify-between" data-testid={`booking-status-${k}`}>
                  <span className="capitalize text-ocean/80">{
                    { pending: "En attente", confirmed: "Acceptées", refused: "Refusées",
                      cancelled_by_user: "Annulées par le participant" }[k] || k
                  }</span>
                  <span className="font-display font-bold text-ocean">{v}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
}
